/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import { Plus, Minus } from 'lucide-react';

/**
 * ProductAccordion — toggle-based accordion (each section independent).
 * Smooth height animation via measured content height.
 *
 * Props:
 *   sections: [{ key, label, content (ReactNode) }, ...]
 *   defaultOpenKey: optional string of key to start expanded
 */
const ProductAccordion = ({ sections = [], defaultOpenKey }) => {
  const [openKeys, setOpenKeys] = useState(() => new Set(defaultOpenKey ? [defaultOpenKey] : []));

  const toggle = (key) => {
    setOpenKeys((curr) => {
      const next = new Set(curr);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="border-t border-[#e5dfd0]">
      {sections.map((sec) => {
        const open = openKeys.has(sec.key);
        return (
          <AccordionItem
            key={sec.key}
            label={sec.label}
            open={open}
            onToggle={() => toggle(sec.key)}
          >
            {sec.content}
          </AccordionItem>
        );
      })}
    </div>
  );
};

const AccordionItem = ({ label, open, onToggle, children }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    if (!contentRef.current) return;
    const measure = () => setHeight(contentRef.current.scrollHeight);
    measure();
    // Re-measure if window resizes (responsive content)
    const ro = new ResizeObserver(measure);
    ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [children]);

  return (
    <div className="border-b border-[#e5dfd0]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-5 text-left group"
      >
        <span className="text-[11px] tracking-[0.32em] uppercase text-[#1a1410]">{label}</span>
        <span className="relative w-5 h-5 flex items-center justify-center">
          <Plus
            className={`w-5 h-5 transition-all duration-300 ${open ? 'rotate-45 opacity-0' : 'rotate-0 opacity-100'}`}
            strokeWidth={1.4}
          />
          <Minus
            className={`w-5 h-5 absolute inset-0 m-auto transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
            strokeWidth={1.4}
          />
        </span>
      </button>
      <div
        className="overflow-hidden transition-[max-height,opacity] duration-400 ease-out"
        style={{
          maxHeight: open ? `${height}px` : '0px',
          opacity: open ? 1 : 0,
          transitionDuration: '400ms',
        }}
        aria-hidden={!open}
      >
        <div ref={contentRef} className="pb-6 text-[15px] leading-[1.8] text-[#3a2e26]">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ProductAccordion;
